import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../services/firebase';
import { clearSessionCookie } from '../utils/sessionAuth';

/**
 * Custom hook for proper user sign-out with state cleanup
 * Handles Firebase sign-out, Apollo cache clearing, and navigation
 */
export const useSignOut = () => {
    const navigate = useNavigate();

    const signOut = useCallback(async (redirectTo: string = '/') => {
        try {
            // 1. Clear session cookie first
            clearSessionCookie();
            
            // 2. Sign out of Firebase
            await auth.signOut();

            // 3. Clear Apollo cache to remove stale data
            // Stop all in-flight queries first, then clear the store immediately
            // Use clearStore() instead of resetStore() - it immediately clears without waiting,
            // which is appropriate for logout scenarios
            try {
                const { apolloClient } = await import('../services/membershipApi');
                
                // Stop all active queries to prevent race conditions
                apolloClient.stop();
                
                // Clear the store immediately without waiting for queries
                await apolloClient.clearStore();
            } catch (apolloError: any) {
                // Suppress expected errors during logout
                if (process.env.NODE_ENV === 'development') {
                    console.debug('Apollo store cleanup had issues during logout:', apolloError.message);
                }
            }
            
            // Ensure Apollo operations complete before proceeding to navigation
            // This prevents race conditions where navigation triggers new queries

            // 4. Clear browser storage selectively
            // Clear payment-related data to prevent reuse errors
            try {
                // Clear any cached payment method IDs or Stripe-related data
                // Note: We're being selective - not clearing everything to avoid breaking other features
                const keysToRemove: string[] = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && (
                        key.includes('payment') || 
                        key.includes('stripe') || 
                        key.includes('card') ||
                        key.includes('billing')
                    )) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key));
                
                // Clear sessionStorage as well
                sessionStorage.clear();
            } catch (storageError) {
                console.warn('Could not clear browser storage:', storageError);
            }

            // 5. Navigate using React Router (maintains SPA behavior)
            navigate(redirectTo, { replace: true });

            return { success: true };

        } catch (error) {
            console.error('Sign out failed:', error);

            // Fallback to full page reload if clearing state fails
            window.location.href = redirectTo;

            return { success: false, error };
        }
    }, [navigate]);

    return { signOut };
};
